import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BarChart, LineChart } from '@mui/x-charts';
import { useNavigate } from 'react-router-dom';
import { getProviderTaskStats } from '../Api';
import ProviderTaskStatsResponseDto, { ProviderTaskStatsDto, ProviderTaskStatsPointDto } from '../models/dtos/ProviderTaskStatsDto';

// History is kept 7 days on the provider
const WINDOWS = [
    { key: '1h', label: 'Last hour', hours: 1 },
    { key: '6h', label: 'Last 6 hours', hours: 6 },
    { key: '24h', label: 'Last 24 hours', hours: 24 },
    { key: '3d', label: 'Last 3 days', hours: 72 },
    { key: '7d', label: 'Last 7 days', hours: 168 },
    { key: 'custom', label: 'Custom', hours: 0 },
];

const BUCKETS = [0, 1, 5, 15, 30, 60, 180, 360, 1440];

type ChartMetric = 'processing' | 'lockWait' | 'interval';

const METRICS: { key: ChartMetric; label: string; hint: string; pick: (p: ProviderTaskStatsPointDto) => number | null }[] = [
    { key: 'processing', label: 'Execution time', hint: 'Average time a run holds the task lock', pick: p => p.avgProcessingMs },
    { key: 'lockWait', label: 'Lock wait', hint: 'Average time a run waits for the task lock', pick: p => p.avgLockWaitMs },
    { key: 'interval', label: 'Interval', hint: 'Average real time between two runs of the task', pick: p => p.avgIntervalMs },
];

const seconds = (ms: number | null | undefined): number | null => ms == null ? null : Math.round(ms / 100) / 10;

const formatSeconds = (ms: number | null | undefined): string => {
    const value = seconds(ms);
    return value == null ? '—' : `${value} s`;
};

const bucketLabel = (minutes: number): string => {
    if (minutes === 0) return 'Auto';
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${minutes / 60} h`;
    return `${minutes / 1440} d`;
};

/** datetime-local value (local time) for an instant */
const toLocalInput = (date: Date): string => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
};

const ProviderTaskStatsPage: React.FC = () => {
    const navigate = useNavigate();
    const [windowKey, setWindowKey] = useState('24h');
    const [customFrom, setCustomFrom] = useState(() => toLocalInput(new Date(Date.now() - 24 * 3600 * 1000)));
    const [customTo, setCustomTo] = useState(() => toLocalInput(new Date()));
    const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
    const [bucketMinutes, setBucketMinutes] = useState(0);
    const [metric, setMetric] = useState<ChartMetric>('processing');
    const [stats, setStats] = useState<ProviderTaskStatsResponseDto | null>(null);
    const [knownTasks, setKnownTasks] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        const selectedWindow = WINDOWS.find(w => w.key === windowKey)!;
        let from: Date;
        let to: Date;
        if (selectedWindow.key === 'custom') {
            from = new Date(customFrom);
            to = new Date(customTo);
            if (isNaN(from.getTime()) || isNaN(to.getTime()) || from >= to) {
                setError('Pick a custom window where "From" is before "To".');
                return;
            }
        } else {
            to = new Date();
            from = new Date(to.getTime() - selectedWindow.hours * 3600 * 1000);
        }
        setLoading(true);
        setError(null);
        try {
            const result = await getProviderTaskStats({
                from: from.toISOString(),
                to: to.toISOString(),
                tasks: selectedTasks,
                bucketMinutes: bucketMinutes || undefined,
            });
            setStats(result);
            setKnownTasks(prev => Array.from(new Set([...prev, ...result.availableTasks])).sort());
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load provider task stats');
        } finally {
            setLoading(false);
        }
    }, [windowKey, customFrom, customTo, selectedTasks, bucketMinutes]);

    useEffect(() => {
        if (windowKey !== 'custom') load();
        // Custom windows load on Apply, so typing a date does not fire requests
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowKey, selectedTasks, bucketMinutes]);

    const tasks: ProviderTaskStatsDto[] = useMemo(() => stats?.tasks ?? [], [stats]);
    const selectedMetric = METRICS.find(m => m.key === metric)!;

    const lineSeries = useMemo(() => tasks.map(task => ({
        label: task.taskName,
        data: task.series.map(point => seconds(selectedMetric.pick(point))),
        showMark: false,
        connectNulls: true,
        valueFormatter: (value: number | null) => value == null ? '—' : `${value} s`,
    })), [tasks, selectedMetric]);

    const timeAxis = useMemo(() => (tasks[0]?.series ?? []).map(point => new Date(point.bucketStart)), [tasks]);

    const barTasks = useMemo(() => [...tasks].sort((a, b) => (b.avgProcessingMs ?? 0) - (a.avgProcessingMs ?? 0)), [tasks]);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">Provider task history</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Scheduled task runs of the xbet provider, kept for 7 days. Execution time is the time a run holds the
                        shared task lock; lock wait is how long it queued for it.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/health')}>Health</Button>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
                </Box>
            </Box>

            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControl sx={{ minWidth: 180 }} size="small">
                            <InputLabel id="provider-task-window">Time window</InputLabel>
                            <Select labelId="provider-task-window" label="Time window" value={windowKey}
                                    onChange={e => setWindowKey(e.target.value)}>
                                {WINDOWS.map(w => <MenuItem key={w.key} value={w.key}>{w.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {windowKey === 'custom' && (
                            <>
                                <TextField label="From" type="datetime-local" size="small" sx={{ minWidth: 220 }} InputLabelProps={{ shrink: true }}
                                           value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
                                <TextField label="To" type="datetime-local" size="small" sx={{ minWidth: 220 }} InputLabelProps={{ shrink: true }}
                                           value={customTo} onChange={e => setCustomTo(e.target.value)} />
                                <Button variant="contained" onClick={load} disabled={loading}>Apply</Button>
                            </>
                        )}
                        <Autocomplete
                            multiple
                            size="small"
                            sx={{ minWidth: 320, flex: 1 }}
                            options={knownTasks}
                            value={selectedTasks}
                            onChange={(_, value) => setSelectedTasks(value)}
                            renderInput={params => <TextField {...params} label="Tasks" placeholder={selectedTasks.length ? '' : 'All tasks'} />}
                        />
                        <FormControl sx={{ minWidth: 140 }} size="small">
                            <InputLabel id="provider-task-bucket">Resolution</InputLabel>
                            <Select labelId="provider-task-bucket" label="Resolution" value={bucketMinutes}
                                    onChange={e => setBucketMinutes(Number(e.target.value))}>
                                {BUCKETS.map(b => <MenuItem key={b} value={b}>{bucketLabel(b)}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </CardContent>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading && !stats ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : stats && tasks.length === 0 ? (
                <Alert severity="info">No task runs recorded in this window.</Alert>
            ) : stats && (
                <Stack spacing={3} sx={{ opacity: loading ? 0.6 : 1 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Box>
                                    <Typography variant="h6">{selectedMetric.label} over time</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedMetric.hint}, seconds, per {bucketLabel(stats.bucketMinutes)}
                                    </Typography>
                                </Box>
                                <ToggleButtonGroup size="small" exclusive value={metric}
                                                   onChange={(_, value) => value && setMetric(value)}>
                                    {METRICS.map(m => <ToggleButton key={m.key} value={m.key}>{m.label}</ToggleButton>)}
                                </ToggleButtonGroup>
                            </Box>
                            <LineChart
                                height={380}
                                xAxis={[{ scaleType: 'time', data: timeAxis }]}
                                yAxis={[{ label: 'seconds' }]}
                                series={lineSeries}
                                margin={{ top: 80 }}
                            />
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6">Average time per task</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Execution time and lock wait per run, seconds, over the whole window
                            </Typography>
                            <BarChart
                                layout="horizontal"
                                height={Math.max(220, barTasks.length * 44 + 80)}
                                yAxis={[{ scaleType: 'band', data: barTasks.map(t => t.taskName) }]}
                                series={[
                                    { label: 'Execution time', data: barTasks.map(t => seconds(t.avgProcessingMs) ?? 0) },
                                    { label: 'Lock wait', data: barTasks.map(t => seconds(t.avgLockWaitMs) ?? 0) },
                                ]}
                                margin={{ left: 250 }}
                            />
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Summary</Typography>
                            <TableContainer>
                                <Table size="small" sx={{ '& th, & td': { whiteSpace: 'nowrap' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Task</TableCell>
                                            <TableCell align="right">Runs</TableCell>
                                            <TableCell align="right">Failed</TableCell>
                                            <TableCell align="right">Avg execution</TableCell>
                                            <TableCell align="right">p95 execution</TableCell>
                                            <TableCell align="right">Max execution</TableCell>
                                            <TableCell align="right">Avg lock wait</TableCell>
                                            <TableCell align="right">Max lock wait</TableCell>
                                            <TableCell align="right">Avg fetch</TableCell>
                                            <TableCell align="right">Avg interval</TableCell>
                                            <TableCell align="right">Max interval</TableCell>
                                            <TableCell align="right">Avg items</TableCell>
                                            <TableCell>Last run</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tasks.map(task => (
                                            <TableRow key={task.taskName} hover>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{task.taskName}</TableCell>
                                                <TableCell align="right">{task.runs}</TableCell>
                                                <TableCell align="right" sx={{ color: task.failedRuns > 0 ? 'error.main' : undefined }}>
                                                    {task.failedRuns}
                                                </TableCell>
                                                <TableCell align="right">{formatSeconds(task.avgProcessingMs)}</TableCell>
                                                <TableCell align="right">{formatSeconds(task.p95ProcessingMs)}</TableCell>
                                                <TableCell align="right">{formatSeconds(task.maxProcessingMs)}</TableCell>
                                                <TableCell align="right">{formatSeconds(task.avgLockWaitMs)}</TableCell>
                                                <TableCell align="right">{formatSeconds(task.maxLockWaitMs)}</TableCell>
                                                <TableCell align="right">{formatSeconds(task.avgFetchMs)}</TableCell>
                                                <TableCell align="right">{formatSeconds(task.avgIntervalMs)}</TableCell>
                                                <TableCell align="right">{formatSeconds(task.maxIntervalMs)}</TableCell>
                                                <TableCell align="right">{task.avgItems == null ? '—' : Math.round(task.avgItems)}</TableCell>
                                                <TableCell>
                                                    {task.lastFinishedAt && (
                                                        <Tooltip title={new Date(task.lastFinishedAt).toLocaleString()}>
                                                            <Chip size="small" variant="outlined"
                                                                  color={task.lastStatus === 'FAILED' ? 'error' : 'success'}
                                                                  label={new Date(task.lastFinishedAt).toLocaleTimeString()} />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Stack>
            )}
        </Container>
    );
};

export default ProviderTaskStatsPage;
